import React from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import GridTable from "../../table/gridTable";
import Loader from "../../SharedLayouts/Loader";
import ExportCsvTable from "../../Export/ExportCsvTable";
import { getCurrencySymbol } from "../../../Helper/getCurrencySymbol";

const trendColor = (trend) =>
  trend === "up" ? "#067647" : trend === "down" ? "#B42318" : "#475467";

function StocksTable() {
  const navigate = useNavigate();
  const allStocks = useSelector((state) => state?.stocks?.allStocks) || [];
  const loading = useSelector((state) => state?.stocks?.getStocksPending);

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "symbol", headerName: "Symbol", width: 120 },
    { field: "name", headerName: "Name", width: 200 },
    {
      field: "current_price",
      headerName: "Current Price",
      width: 150,
      renderCell: (params) =>
        `${getCurrencySymbol(params.row?.currency)} ${
          params.row?.current_price ?? "-"
        }`,
    },
    {
      field: "available_volume",
      headerName: "Available Volume",
      width: 150,
    },
    {
      field: "percent_change",
      headerName: "% Change",
      width: 120,
      renderCell: (params) => (
        <span style={{ color: trendColor(params.row?.trend) }}>
          {params.row?.percent_change ?? 0}%
        </span>
      ),
    },
    {
      field: "trend",
      headerName: "Trend",
      width: 100,
      renderCell: (params) => (
        <span
          style={{ color: trendColor(params.row?.trend), textTransform: "capitalize" }}
        >
          {params.row?.trend || "-"}
        </span>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <div
          style={{
            display: "inline-block",
            padding: "0 8px",
            height: "22px",
            borderRadius: "10px",
            textAlign: "center",
            lineHeight: "22px",
            color: params.row?.is_active ? "#067647" : "#B42318",
            backgroundColor: params.row?.is_active ? "#ECFDF3" : "#FECDCA",
          }}
        >
          {params.row?.is_active ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "is_shariah_compliant",
      headerName: "Shariah",
      width: 110,
      renderCell: (params) => (params.row?.is_shariah_compliant ? "Yes" : "No"),
    },
  ];

  const handleRowClick = (params) => {
    navigate(`/stocks/${params?.row?.id}`);
  };

  const csvHeaders = [
    "ID",
    "Symbol",
    "Name",
    "Current Price",
    "Available Volume",
    "% Change",
    "Trend",
    "Status",
    "Shariah",
  ];

  const csvRows = allStocks.map((row) => [
    row?.id,
    row?.symbol,
    row?.name,
    `${getCurrencySymbol(row?.currency)} ${row?.current_price ?? "-"}`,
    row?.available_volume,
    `${row?.percent_change ?? 0}%`,
    row?.trend || "-",
    row?.is_active ? "Active" : "Inactive",
    row?.is_shariah_compliant ? "Yes" : "No",
  ]);

  if (loading && allStocks?.length < 1) {
    return <Loader />;
  }

  return (
    <Box sx={{ overflowX: "auto", width: "100%", cursor: "pointer" }}>
      <div className="flex justify-end mb-2">
        <ExportCsvTable
          headers={csvHeaders}
          rows={csvRows}
          filename="Stocks.csv"
        />
      </div>
      <GridTable
        rows={allStocks}
        columns={columns}
        type={"stocks"}
        searchPlaceholder={"Search for Stocks"}
        noReport
        handleRowClick={handleRowClick}
      />
    </Box>
  );
}

export default StocksTable;
